import { useMutation, useQuery } from '@apollo/client';
import {
  MutationResponse,
  SendVerificationEmailInput,
  UpdateUserProfileInput,
  User,
} from '@bybeam/platform-types';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useErrorIndicator from '@platform-ui-common/hooks/useErrorIndicator.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import SendVerificationEmail from '@platform-ui/application/graphql/SendVerificationEmailMutation.graphql';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import GetAuthenticatedPayeeDetailsQuery from '@platform-ui/claim/graphql/GetAuthenticatedPayeeDetails.graphql';
import {
  getApplicantProfileConfig,
  mapAdditionalFields,
  mapKnownFields,
} from '@platform-ui/utilities/applicantProfile.js';
import UpdateUserProfile from '../graphql/UpdateUserProfileMutation.graphql';

export default function useProfile() {
  const {
    user,
    identity: { tenantId },
  } = useAuth();

  const payeeQuery = useQuery<{ currentUser: User }>(GetAuthenticatedPayeeDetailsQuery, {
    variables: { includeBankAccount: true, includeMailingAddress: true },
  });

  const {
    id: partnerId,
    applicantProfileConfigs,
    communicationChannels,
    features,
    ...partner
  } = usePartner();
  const { programs } = usePartnerPrograms();
  const partnerConfig = getApplicantProfileConfig(
    applicantProfileConfigs,
    user.core.applicantProfile,
  );
  const renderApplicantProfileFields = () => {
    return {
      knownFields: mapKnownFields(user.core, partnerConfig, programs),
      additionalFields: mapAdditionalFields(user.core, partnerConfig, programs),
    };
  };

  const [updateProfile, updateProfileResults] = useMutation<
    { user: { updateProfile: MutationResponse<User> } },
    { input: UpdateUserProfileInput }
  >(UpdateUserProfile);

  const [verifyEmail, verificationEmailResults] = useMutation<
    { identity: { sendVerificationEmail: MutationResponse } },
    { input: SendVerificationEmailInput }
  >(SendVerificationEmail);

  const { showSnackbar } = useSnackbar();
  const showError = useErrorIndicator();

  const sendEmail = () =>
    verifyEmail({
      variables: {
        input: {
          partnerId,
          email: user.core.email,
          tenantId,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    }).then((response) => {
      const status = response?.data?.identity?.sendVerificationEmail?.metadata?.status;
      if (status === 200)
        return showSnackbar(
          <p>
            Email sent to <b>{user.core.email}</b>
          </p>,
        );
      if (status >= 400 || response?.errors.length)
        return showError('There was an error sending the email', sendEmail);
    });

  return {
    user,
    payee: {
      loading: payeeQuery.loading,
      bankAccount: payeeQuery?.data?.currentUser?.bankAccount,
    },
    renderApplicantProfileFields,
    updateProfile: {
      results: updateProfileResults,
      update: updateProfile,
    },
    verificationEmail: {
      loading: verificationEmailResults.loading,
      send: sendEmail,
    },
    partner: {
      name: partner?.name,
      email: partner?.email,
      phone: partner?.phone,
      programs: programs ?? [],
      communicationChannels: {
        channels: communicationChannels?.config?.channels ?? {},
      },
      features,
    },
  };
}
